/**
 * PromoterImpactHighlights.tsx - Impact Highlights for Promoter Invite Page
 * 
 * This component displays impact statistics specifically designed for the promoter
 * invitation page with mobile-optimized layout and promoter-focused messaging.
 * 
 * Key Features:
 * - Mobile-first responsive design (2x2 grid on mobile, 4x1 on desktop)
 * - Promoter-focused impact messaging and statistics
 * - Custom theming integration with fundraiser colors
 * - Optimized spacing and typography for invite page
 * - Enhanced visual hierarchy for conversion optimization
 * 
 * Layout Behavior:
 * - Desktop: 4 cards in a single row
 * - Mobile: 2 cards across, 2 rows down (2x2 grid)
 * - Tablet: Responsive breakpoint handling
 * 
 * Props:
 * - customColors?: any - Custom color theming from fundraiser
 * 
 * Dependencies: MobX store, Lucide icons, styled-components
 */

import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import * as Icons from 'lucide-react';
import { fundraiserStore } from '../../stores/FundraiserStore';
import { ImpactModel } from '../../models/FundraisersGridModels';

const ImpactContainer = styled.div`
  padding: 0;
  margin: 0;
  text-align: center;
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const ImpactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 24px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  min-height: 200px;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 24px 16px;
    min-height: 180px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 12px;
    min-height: 160px;
    gap: 8px;
  }
`;

const ImpactIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
  margin-bottom: 8px;
  box-shadow: 0 4px 16px ${props => props.color}30;
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    margin-bottom: 4px;
  }
`;

const ImpactNumber = styled.div<{ color: string }>`
  font-size: 32px;
  font-weight: 800;
  color: ${props => props.color};
  margin-bottom: 8px;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 4px;
  }
`;

const ImpactLabel = styled.div<{ color: string }>`
  font-size: 16px;
  color: ${props => props.color};
  font-weight: 600;
  text-align: center;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.2;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
`;

const EmptyTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

interface PromoterImpactHighlightsProps {
  customColors?: any;
  impacts: ImpactModel[];
}

export const PromoterImpactHighlights: React.FC<PromoterImpactHighlightsProps> = observer(({ customColors, impacts }) => {

  const renderIcon = (iconName: string) => {
    const Icon = (Icons as any)[iconName];
    const iconSize = window.innerWidth <= 480 ? 20 : window.innerWidth <= 768 ? 24 : 28;
    return Icon ? <Icon size={iconSize} /> : <Icons.Heart size={iconSize} />;
  };

  // If no impacts are defined, don't render anything
  if (impacts.length === 0) {
    return (
      <ImpactContainer>
        <EmptyState>
          <EmptyTitle>No Impact Items</EmptyTitle>
          <EmptyText>
            Add impact items in the Fundraiser Details step to showcase your mission's impact.
          </EmptyText>
        </EmptyState>
      </ImpactContainer>
    );
  }

  return (
    <ImpactContainer>
      <ImpactGrid>
        {impacts.map((item, index) => (
          <ImpactItem key={item.id || index}>
            <ImpactIcon color={item.color?.hex || ''}>
              {renderIcon(item.icon)}
            </ImpactIcon>
            <ImpactNumber color={item.color?.hex || ''}>{item.number}</ImpactNumber>
            <ImpactLabel color={item.color?.hex || ''}>{item.description}</ImpactLabel>
          </ImpactItem>
        ))}
      </ImpactGrid>
    </ImpactContainer>
  );
});